import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FileText, LayoutGrid, ChevronRight, CheckSquare, FileSearch, MessageCircle, X } from 'lucide-react';
import { ArborVisualization } from '../../components/ArborVisualization';
import { PageHeader } from '../../components/PageHeader';
import { IndicatorCard } from '../../components/IndicatorCard';
import { AppleHealthCard } from '../../features/illuminate/AppleHealthCard';
import { SAMPLE_HEALTH_PROFILE } from '../../features/illuminate/sample-health-profile';
import { useScrollRestore } from '../../hooks/useScrollRestore';
import {
  useWatchedMetrics,
  useBiologicalAge,
  useReports,
} from '../../features/illuminate/hooks';
import { useSampleDataStore } from '../../stores/sample-data-store';
import styles from './HealthDashboardPage.module.css';

const stagger = {
  container: {
    animate: { transition: { staggerChildren: 0.08 } },
  },
  item: {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } },
  },
};

export function HealthDashboardPage() {
  const navigate = useNavigate();
  useScrollRestore('illuminate');
  const { data: biologicalAge, loading: bioAgeLoading, error: bioAgeError } = useBiologicalAge();
  const { data: watchedMetrics, loading: watchedLoading, error: watchedError, isEmpty: noWatched, refetch: refetchWatched } = useWatchedMetrics();
  const { data: reports, loading: reportsLoading } = useReports();
  const sampleEnabled = useSampleDataStore(s => s.enabled);
  const hasRealDataSeed = useSampleDataStore(s => s.hasRealDataSeed);
  const enableSampleData = useSampleDataStore(s => s.enable);
  const disableSampleData = useSampleDataStore(s => s.disable);
  const hasBioAge = (biologicalAge?.overallAge ?? 0) > 0;
  const hasReports = (reports ?? []).length > 0;
  const baselineLoaded = !watchedLoading && !bioAgeLoading && !reportsLoading;
  const hasRealBaseline = hasReports || hasBioAge || !noWatched || hasRealDataSeed;
  const isBaselineEmpty = baselineLoaded && noWatched && !hasBioAge && !hasReports;
  const shouldShowSample = baselineLoaded && sampleEnabled && !hasRealBaseline;
  const shouldShowEmptyStart = isBaselineEmpty && !hasRealBaseline && !shouldShowSample;
  const shouldShowRealSeedStart = isBaselineEmpty && hasRealDataSeed;

  return (
    <motion.div
      className={styles.container}
      variants={stagger.container}
      initial="initial"
      animate="animate"
    >
      <PageHeader mode="primary" title="照见" subtitle="生理年龄与健康档案" showProfile profileHref="/profile" />

      <motion.div
        variants={stagger.item}
        className={styles.arborContainer}
      >
        <ArborVisualization level={100} className={styles.arborCanvas} />
        <button
          type="button"
          className={`${styles.scorePill} ${!shouldShowSample ? styles.scorePillInteractive : ''}`}
          onClick={() => { if (!shouldShowSample) navigate('/illuminate/biological-age'); }}
          aria-label={shouldShowSample ? '当前为示例数据' : '查看生理年龄详情'}
        >
          <span className={styles.scoreValue}>
            {shouldShowSample
              ? '示例'
              : bioAgeLoading && !biologicalAge
              ? '—'
              : hasBioAge
                ? biologicalAge!.overallAge
                : '—'}
          </span>
          <span className={styles.scoreLabel}>
            {shouldShowSample
              ? '示例数据'
              : bioAgeLoading && !biologicalAge
              ? '评估中'
              : hasBioAge
                ? '生理年龄'
                : hasRealDataSeed
                  ? '资料已收'
                  : (bioAgeError ? '评估未完成' : '等待资料')}
          </span>
        </button>
      </motion.div>

      <motion.div variants={stagger.item}>
        <AppleHealthCard />
      </motion.div>

      {shouldShowEmptyStart && (
        <motion.section variants={stagger.item} className={styles.emptyStartPanel}>
          <p className={styles.emptyStartKicker}>建立第一份生命基线</p>
          <h2 className={styles.emptyStartTitle}>先让 AETERNA 认识你的健康状态</h2>
          <p className={styles.emptyStartDesc}>
            有体检报告时，上传后可自动提取指标并追踪趋势；暂时没有报告，也可以先用快速评估获得一个粗略起点。
          </p>
          <div className={styles.emptyStartActions}>
            <button type="button" className={`${styles.emptyStartAction} ${styles.emptyStartActionPrimary}`} onClick={() => navigate('/illuminate/reports')}>
              <FileText size={18} strokeWidth={1.6} />
              <span>上传体检报告</span>
            </button>
            <button type="button" className={`${styles.emptyStartAction} ${styles.emptyStartActionSecondary}`} onClick={() => navigate('/assess/hub')}>
              <CheckSquare size={18} strokeWidth={1.6} />
              <span>快速评估</span>
            </button>
          </div>
          <button type="button" className={styles.emptyStartSampleButton} onClick={enableSampleData}>
            <FileSearch size={16} strokeWidth={1.6} />
            <span>查看示例</span>
          </button>
          <p className={styles.emptyStartNote}>上传或评估完成后，首页会自动切换为你的指标与趋势视图。</p>
        </motion.section>
      )}

      {shouldShowSample && (
        <motion.section variants={stagger.item} className={styles.samplePanel}>
          <div className={styles.sampleBanner}>
            <span>示例数据 · 不代表你的真实健康状态</span>
            <button type="button" className={styles.sampleClearButton} onClick={disableSampleData} aria-label="清除示例数据">
              <X size={16} strokeWidth={1.8} />
            </button>
          </div>
          <div className={styles.sampleSummary}>
            <p className={styles.emptyStartKicker}>示例健康档案</p>
            <h2 className={styles.emptyStartTitle}>{SAMPLE_HEALTH_PROFILE.title}</h2>
            <p className={styles.emptyStartDesc}>{SAMPLE_HEALTH_PROFILE.summary}</p>
          </div>
          <div className={styles.metricsGrid}>
            {SAMPLE_HEALTH_PROFILE.metrics.map(metric => (
              <motion.div key={metric.key} variants={stagger.item} className={styles.metricsGridItem}>
                <IndicatorCard
                  name={metric.label}
                  value={metric.value}
                  unit={metric.unit}
                  status={metric.status}
                  refRange={metric.refRange}
                  date={metric.updatedAt}
                />
              </motion.div>
            ))}
          </div>
          <div className={styles.sampleRecommendations}>
            {SAMPLE_HEALTH_PROFILE.recommendations.map(item => (
              <div key={item.title} className={styles.sampleRecommendation}>
                <h3 className={styles.sampleRecommendationTitle}>{item.title}</h3>
                <p className={styles.sampleRecommendationDesc}>{item.description}</p>
              </div>
            ))}
          </div>
          <div className={styles.sampleQa}>
            <div className={styles.sampleQaIcon}>
              <MessageCircle size={18} strokeWidth={1.6} />
            </div>
            <div>
              <p className={styles.sampleQaQuestion}>{SAMPLE_HEALTH_PROFILE.sampleQa.question}</p>
              <p className={styles.sampleQaAnswer}>{SAMPLE_HEALTH_PROFILE.sampleQa.answer}</p>
            </div>
          </div>
        </motion.section>
      )}

      {shouldShowRealSeedStart && (
        <motion.section variants={stagger.item} className={styles.emptyStartPanel}>
          <p className={styles.emptyStartKicker}>真实资料已建立</p>
          <h2 className={styles.emptyStartTitle}>我们已收到你的健康资料</h2>
          <p className={styles.emptyStartDesc}>
            AETERNA 已记录你的报告或评估起点。等解析和指标生成完成后，首页会补齐趋势与更具体的健康建议。
          </p>
        </motion.section>
      )}

      {!shouldShowEmptyStart && !shouldShowSample && !shouldShowRealSeedStart && (
      <motion.div variants={stagger.item} className={styles.sectionWithBorderTop}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>关注的指标</h2>
          <button
            onClick={() => navigate('/illuminate/dimensions')}
            className={styles.sectionLink}
          >
            维度矩阵 →
          </button>
        </div>
        {watchedLoading ? (
          <div className={styles.loadingState}>加载关注指标中…</div>
        ) : watchedError ? (
          <div className={styles.emptyMetrics}>
            <p className={styles.emptyMetricsText}>关注指标暂时无法加载</p>
            <button type="button" onClick={() => { void refetchWatched(); }} className={styles.emptyMetricsButton}>
              重试
            </button>
          </div>
        ) : noWatched ? (
          <div className={styles.emptyMetrics}>
            <p className={styles.emptyMetricsText}>
              尚未关注任何指标
            </p>
            <button
              type="button"
              onClick={() => navigate('/illuminate/dimensions')}
              className={styles.emptyMetricsButton}
            >
              前往维度矩阵关注指标
            </button>
          </div>
        ) : (
          <div className={styles.metricsGrid}>
            {watchedMetrics.map((m) => (
              <motion.div key={m.key} variants={stagger.item} className={styles.metricsGridItem}>
                <IndicatorCard
                  name={m.label}
                  value={m.value}
                  unit={m.unit}
                  status={m.status}
                  date={m.updatedAt ?? undefined}
                  onClick={() => navigate(`/illuminate/metric/${m.key}`)}
                />
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
      )}

      <motion.div variants={stagger.item} className={styles.sectionWithBorderTop}>
        <h2 className={styles.sectionTitle}>快速入口</h2>
        <nav className={styles.navList}>
          {[
            { icon: FileText, label: '体检文档库', path: '/illuminate/reports' },
            { icon: LayoutGrid, label: '维度矩阵', path: '/illuminate/dimensions' },
            { icon: CheckSquare, label: '健康评估', path: '/assess/hub' },
          ].map(item => (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className={styles.navItem}
            >
              <div className={styles.navIcon}>
                <item.icon size={18} color="var(--color-ash)" strokeWidth={1.5} />
              </div>
              <span className={styles.navLabel}>{item.label}</span>
              <ChevronRight size={16} color="var(--color-ash)" strokeWidth={1.5} />
            </button>
          ))}
        </nav>
      </motion.div>
    </motion.div>
  );
}
